import React from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import LoginPage from './components/LoginPage';
import ChatApp from './components/ChatApp';
import ErrorBoundary from './components/common/ErrorBoundary';
import { AuthProvider, useAuthContext } from './hooks/AuthContext';
import { AdminAccessProvider } from './hooks/AdminAccessContext';
import { getDefaultAuthenticatedRoute, rememberIntendedRoute, resolvePostAuthRoute } from './utils/authRedirect';
import AdminRoute from './admin/AdminRoute';
import AdminLayout from './admin/AdminLayout';
import AdminDashboard from './admin/AdminDashboard';
import AdminPlaceholderPage from './admin/AdminPlaceholderPage';
import AdminUsersPage from './admin/AdminUsersPage';
import AdminUserDetailPage from './admin/AdminUserDetailPage';
import AdminCreatorStudioPage from './admin/AdminCreatorStudioPage';
import AdminContentHome from './admin/AdminContentHome';
import AdminContentCollectionPage from './admin/AdminContentCollectionPage';
import AdminSessionsPage from './admin/AdminSessionsPage';
import AdminSessionDetailPage from './admin/AdminSessionDetailPage';
import AdminSessionErrorsPage from './admin/AdminSessionErrorsPage';
import AdminPaymentsPage from './admin/AdminPaymentsPage';
import AdminAnalyticsPage from './admin/AdminAnalyticsPage';
import AdminAuditLogPage from './admin/AdminAuditLogPage';
import AdminOpsPage from './admin/AdminOpsPage';

const AuthStatusScreen = ({ message = 'Checking your session...' }) => (
  <div className="login-background cq-login-shell">
    <div className="app-container login-container cq-login-container">
      <section className="cq-login-panel cq-login-panel--loading">
        <h1 className="cq-login-panel__title">Cringe Quest</h1>
        <p className="cq-login-panel__copy">{message}</p>
      </section>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { authenticatedUser, authLoading } = useAuthContext();
  const location = useLocation();

  if (authLoading) {
    return <AuthStatusScreen />;
  }

  if (!authenticatedUser) {
    rememberIntendedRoute(location);
    return <Navigate to="/auth" replace state={{ from: location }} />;
  }

  return children;
};

const AuthRoute = () => {
  const { authenticatedUser, authLoading } = useAuthContext();
  const location = useLocation();

  if (authLoading) {
    return <AuthStatusScreen />;
  }

  if (authenticatedUser) {
    return <Navigate to={resolvePostAuthRoute(location.state)} replace />;
  }

  return <LoginPage />;
};

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<Navigate to={getDefaultAuthenticatedRoute()} replace />} />
    <Route path="/auth" element={<AuthRoute />} />
    <Route path="/login" element={<Navigate to="/auth" replace />} />
    <Route path="/auth-callback" element={<Navigate to="/auth" replace />} />
    <Route
      path="/chat"
      element={(
        <ProtectedRoute>
          <ErrorBoundary
            title="Game Display Error"
            message="The game view hit a display error. The session may still be running; try recovering the view or reload if needed."
            errorCategory="chat_route_boundary"
          >
            <ChatApp />
          </ErrorBoundary>
        </ProtectedRoute>
      )}
    />
    <Route
      path="/admin"
      element={(
        <AdminRoute>
          <AdminLayout />
        </AdminRoute>
      )}
    >
      <Route index element={<AdminDashboard />} />
      <Route path="users" element={<AdminUsersPage />} />
      <Route path="users/:userId" element={<AdminUserDetailPage />} />
      <Route path="sessions" element={<AdminSessionsPage />} />
      <Route path="sessions/:sessionId" element={<AdminSessionDetailPage />} />
      <Route path="sessions/errors" element={<AdminSessionErrorsPage />} />
      <Route path="content" element={<AdminContentHome />} />
      <Route path="content/game-packs" element={<AdminContentCollectionPage entityType="game-packs" />} />
      <Route path="content/game-modes" element={<AdminContentCollectionPage entityType="game-modes" />} />
      <Route path="content/quest-packs" element={<AdminContentCollectionPage entityType="quest-packs" />} />
      <Route path="content/questions" element={<AdminContentCollectionPage entityType="questions" />} />
      <Route path="content/hosts" element={<AdminContentCollectionPage entityType="hosts" />} />
      <Route path="creator-studio" element={<AdminCreatorStudioPage />} />
      <Route path="payments" element={<AdminPaymentsPage view="overview" />} />
      <Route path="payments/wallets" element={<AdminPaymentsPage view="wallets" />} />
      <Route path="payments/transactions" element={<AdminPaymentsPage view="transactions" />} />
      <Route path="payments/stripe-events" element={<AdminPaymentsPage view="issues" />} />
      <Route path="payments/products" element={<AdminPaymentsPage view="products" />} />
      <Route path="payments/stripe-payments" element={<AdminPaymentsPage view="payments" />} />
      <Route path="analytics" element={<AdminAnalyticsPage />} />
      <Route path="ops" element={<AdminOpsPage />} />
      <Route path="settings" element={<AdminPlaceholderPage title="Settings" summary="Operational settings and feature flags will live here." />} />
      <Route path="audit-log" element={<AdminAuditLogPage />} />
    </Route>
    <Route path="*" element={<Navigate to={getDefaultAuthenticatedRoute()} replace />} />
  </Routes>
);

const App = () => (
  <AuthProvider>
    <AdminAccessProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AdminAccessProvider>
  </AuthProvider>
);

export default App;
